import { createTrackingPageBlockId, trackingPageGridConfigs, trackingPageGridRowHeight } from '@reveni-io/reveni-ui'

import { previewViewModes } from '@/configuration/pages/ConfigurationPage/OrderTrackingPageConfiguration/constants'
import { getBlockDefinition } from '@/configuration/pages/ConfigurationPage/OrderTrackingPageConfiguration/helpers'

const desktopCols = trackingPageGridConfigs[previewViewModes.desktop].cols

const getBlockLayoutConstraints = (definition, viewMode) => {
  if (viewMode === previewViewModes.mobile) return { h: 1 }

  return { h: 1, minW: definition.minSize, maxW: desktopCols }
}

const buildViewLayout = (savedLayout, viewMode) => {
  return savedLayout[viewMode].map(item => {
    const definition = getBlockDefinition(item.id)

    return { i: item.id, x: item.x, y: item.y, w: item.w, ...getBlockLayoutConstraints(definition, viewMode) }
  })
}

export const buildLayoutsFromSavedLayout = savedLayout => {
  return {
    [previewViewModes.desktop]: buildViewLayout(savedLayout, previewViewModes.desktop),
    [previewViewModes.mobile]: buildViewLayout(savedLayout, previewViewModes.mobile),
  }
}

const getLayoutBottom = layout => layout.reduce((maxBottom, item) => Math.max(maxBottom, item.y + item.h), 0)

const pushLayoutDown = (layout, rows) => layout.map(item => ({ ...item, y: item.y + rows }))

export const addBlockToLayout = (layout, blockId, viewMode) => {
  const definition = getBlockDefinition(blockId)
  const width = viewMode === previewViewModes.mobile ? 1 : definition.defaultSize
  const constraints = getBlockLayoutConstraints(definition, viewMode)
  const newItem = { i: blockId, x: 0, w: width, y: getLayoutBottom(layout), ...constraints }

  if (definition.insertAtTop) return [{ ...newItem, y: 0 }, ...pushLayoutDown(layout, newItem.h)]

  return [...layout, newItem]
}

export const addBlockToLayouts = (layouts, blockId) => {
  return {
    [previewViewModes.desktop]: addBlockToLayout(layouts[previewViewModes.desktop], blockId, previewViewModes.desktop),
    [previewViewModes.mobile]: addBlockToLayout(layouts[previewViewModes.mobile], blockId, previewViewModes.mobile),
  }
}

export const createBlockId = blockId => createTrackingPageBlockId(getBlockDefinition(blockId))

export const removeBlockFromLayout = (layout, blockId) => layout.filter(item => item.i !== blockId)

export const removeBlockFromLayouts = (layouts, blockId) => {
  return {
    [previewViewModes.desktop]: removeBlockFromLayout(layouts[previewViewModes.desktop], blockId),
    [previewViewModes.mobile]: removeBlockFromLayout(layouts[previewViewModes.mobile], blockId),
  }
}

const cloneLayout = layout => layout.map(item => ({ ...item }))

export const cloneLayouts = layouts => {
  return {
    [previewViewModes.desktop]: cloneLayout(layouts[previewViewModes.desktop]),
    [previewViewModes.mobile]: cloneLayout(layouts[previewViewModes.mobile]),
  }
}

export const updateLayoutItemHeight = (layout, blockId, height) => {
  const rows = Math.max(1, Math.ceil(height / trackingPageGridRowHeight))
  const layoutItem = layout.find(item => item.i === blockId)

  if (!layoutItem || layoutItem.h === rows) return layout

  return layout.map(item => (item.i === blockId ? { ...item, h: rows } : item))
}

export const updateLayoutItemHeights = (layout, blockHeights) => {
  return Object.entries(blockHeights).reduce((updatedLayout, [blockId, height]) => {
    return updateLayoutItemHeight(updatedLayout, blockId, height)
  }, layout)
}

export const keepLayoutItemHeights = (layout, previousLayout) => {
  return layout.map(item => {
    const previousItem = previousLayout.find(previous => previous.i === item.i)

    if (!previousItem || previousItem.h === item.h) return item

    return { ...item, h: previousItem.h }
  })
}

export const compareLayoutItems = (a, b) => a.y - b.y || a.x - b.x

const getLayoutOrder = layout => {
  const sortedItems = [...layout].sort(compareLayoutItems)

  return sortedItems.map(item => ({ i: item.i, x: item.x, w: item.w }))
}

export const haveLayoutsChanged = (layouts, referenceLayouts) => {
  return Object.keys(layouts).some(viewMode => {
    return (
      JSON.stringify(getLayoutOrder(layouts[viewMode])) !== JSON.stringify(getLayoutOrder(referenceLayouts[viewMode]))
    )
  })
}

export const keepLayoutsItemHeights = (layouts, previousLayouts) => {
  if (!previousLayouts) return layouts

  return {
    [previewViewModes.desktop]: keepLayoutItemHeights(
      layouts[previewViewModes.desktop],
      previousLayouts[previewViewModes.desktop]
    ),
    [previewViewModes.mobile]: keepLayoutItemHeights(
      layouts[previewViewModes.mobile],
      previousLayouts[previewViewModes.mobile]
    ),
  }
}
